// src/components/PasswordResetConfirm.js - Password reset confirmation with new password
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import Icon from './Icon';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function PasswordResetConfirm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Get token from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      validateToken(tokenParam);
    } else {
      setError('Ingen återställningslänk hittades');
      setValidating(false);
    }
  }, [location]);

  const validateToken = async (tokenToValidate) => {
    try {
      const response = await axios.get(`${API_BASE}/auth/password-reset/validate/${tokenToValidate}`);
      setTokenValid(response.data.valid);
      if (!response.data.valid) {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('Token validation error:', err);
      setTokenValid(false);
      setError('Ogiltig återställningslänk');
    } finally {
      setValidating(false);
    }
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return 'Lösenordet måste vara minst 8 tecken långt';
    }
    if (!/(?=.*[a-z])/.test(password)) {
      return 'Lösenordet måste innehålla minst en liten bokstav';
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      return 'Lösenordet måste innehålla minst en stor bokstav';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Lösenordet måste innehålla minst en siffra';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Lösenorden matchar inte');
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API_BASE}/auth/password-reset/verify`, {
        token: token,
        new_password: newPassword
      });

      console.log('✅ Password reset successful');
      setSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Lösenordet har återställts. Du kan nu logga in med ditt nya lösenord.' }
        });
      }, 3000);

    } catch (err) {
      console.error('❌ Password reset error:', err);
      setError(err.response?.data?.detail || 'Ett fel uppstod vid återställning av lösenord');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Validerar återställningslänk...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <Icon name="XCircle" className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ogiltig länk</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-yellow-800 text-sm">
              Återställningslänkar är giltiga i endast 15 minuter av säkerhetsskäl.
            </p>
          </div>
          
          <Button 
            onClick={() => navigate('/password-reset')}
            variant="primary"
            className="w-full"
          >
            Begär ny återställningslänk
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <Icon name="CheckCircle" className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Lösenord återställt!</h2>
          <p className="text-gray-600 mb-6">
            Ditt lösenord har ändrats framgångsrikt. Du omdirigeras till inloggningssidan...
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-800 text-sm">
              Av säkerhetsskäl loggas du ut från alla enheter.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
      <div className="text-center mb-6">
        <Icon name="LockKeyhole" className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Skapa nytt lösenord</h2>
        <p className="text-gray-600">
          Ange ditt nya lösenord nedan. Se till att det är starkt och unikt.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Nytt lösenord
          </label>
          <input
            type="password"
            placeholder="Minst 8 tecken"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Bekräfta lösenord
          </label>
          <input
            type="password"
            placeholder="Ange lösenordet igen"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        {error && (
          <div className="bg-red-100 text-red-800 p-3 rounded border border-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-blue-900 mb-2">Lösenordskrav:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>
              {newPassword.length >= 8 ? '✓' : '○'} Minst 8 tecken
            </li>
            <li className={/(?=.*[a-z])/.test(newPassword) ? 'text-green-600' : ''}>
              {/(?=.*[a-z])/.test(newPassword) ? '✓' : '○'} En liten bokstav
            </li>
            <li className={/(?=.*[A-Z])/.test(newPassword) ? 'text-green-600' : ''}>
              {/(?=.*[A-Z])/.test(newPassword) ? '✓' : '○'} En stor bokstav
            </li>
            <li className={/(?=.*\d)/.test(newPassword) ? 'text-green-600' : ''}>
              {/(?=.*\d)/.test(newPassword) ? '✓' : '○'} En siffra
            </li>
          </ul>
        </div>
        
        <Button 
          type="submit" 
          disabled={loading || !newPassword || !confirmPassword} 
          variant="primary" 
          className="w-full"
        >
          {loading ? 'Återställer...' : 'Återställ lösenord'}
        </Button>
      </form>

      <div className="text-center mt-6 pt-4 border-t border-gray-200">
        <button 
          onClick={() => navigate('/login')}
          className="text-blue-600 hover:text-blue-700 underline text-sm"
        >
          ← Tillbaka till inloggning
        </button>
      </div>
    </div>
  );
}

export default PasswordResetConfirm;
